import { useEffect, useState } from 'react';
import { Alert, Button, LinearProgress } from '@mui/material';
import { Box, Stack, Typography } from './mui-system';
import { EditorOnly } from './EditorOnly';
import { api } from '../api/client';
import { fakeDate, fakeError } from './fakes-presentation';
import type { PageCapture, PageCapturesView } from '../../../api/src/fakes/serp-page.types';
const labels: Record<PageCapture['status'], string> = {
  pending: 'В очереди',
  processing: 'Получаем страницу',
  ready: 'Содержимое получено',
  failed: 'Не удалось получить содержимое',
  interrupted: 'Загрузка прервана',
};
export function SerpPageContent({
  workspaceId,
  resultId,
}: {
  workspaceId: string;
  resultId: string;
}) {
  const [data, setData] = useState<PageCapturesView | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    async function read() {
      try {
        const next = await api.serpPageCaptures(workspaceId, resultId);
        if (cancelled) return;
        setData(next);
        setError('');
        if (next.latest && ['pending', 'processing'].includes(next.latest.status))
          timer = setTimeout(() => void read(), 2_000);
      } catch (e) {
        if (!cancelled) setError(fakeError(e));
      }
    }
    void read();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [workspaceId, resultId, revision]);
  async function capture() {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await api.captureSerpPage(
        workspaceId,
        resultId,
        data?.latest ? { refresh: true, expectedId: data.latest.id } : {},
      );
      setRevision((n) => n + 1);
    } catch (e) {
      setError(fakeError(e));
    } finally {
      setBusy(false);
    }
  }
  const latest = data?.latest;
  const pending = latest && ['pending', 'processing'].includes(latest.status);
  const saved = data?.lastSuccessful ?? latest;
  const document = saved?.document;
  return (
    <Stack gap={1} aria-label="Содержимое страницы">
      <Typography fontWeight={700}>Содержимое страницы</Typography>
      {!data && !error && <LinearProgress aria-label="Чтение сохранённого содержимого" />}
      {data && (
        <Typography fontSize={12} color="text.secondary">
          {latest ? labels[latest.status] : 'Страница ещё не загружалась.'}
        </Typography>
      )}
      {pending && <LinearProgress aria-label="Загрузка страницы" />}
      {latest && (
        <Typography fontSize={12} color="text.secondary">
          Запуск: {fakeDate(latest.createdAt)}
        </Typography>
      )}
      {latest?.status === 'failed' && (
        <Alert severity="warning">
          {latest.document?.error?.message ?? 'Ошибка получения страницы'}
        </Alert>
      )}
      {latest?.status === 'interrupted' && (
        <Alert severity="warning">
          Работа прервалась. Автоматического повтора нет; можно запустить загрузку снова.
        </Alert>
      )}
      {saved && latest && saved.id !== latest.id && (
        <Typography fontSize={12}>
          Показан прежний успешный снимок от {fakeDate(saved.finishedAt)}.
        </Typography>
      )}
      {document && (
        <>
          <Typography fontSize={12} sx={{ overflowWrap: 'anywhere' }}>
            {document.httpStatus ? 'Адрес ответа' : 'Адрес запроса'}: {document.finalUrl}
          </Typography>
          {document.content?.title && (
            <Typography fontWeight={600}>{document.content.title}</Typography>
          )}
          {document.content?.h1 && <Typography fontSize={13}>H1: {document.content.h1}</Typography>}
          {document.content?.description && (
            <Typography fontSize={13}>{document.content.description}</Typography>
          )}
          {document.content?.textTruncated && (
            <Alert severity="info">Текст ограничен первыми 60 000 символами.</Alert>
          )}
          {document.content?.text && (
            <Box component="details">
              <Box component="summary" sx={{ cursor: 'pointer' }}>
                Прочитать сохранённый текст
              </Box>
              <Typography
                component="pre"
                fontSize={12}
                sx={{
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                  maxHeight: 360,
                  overflow: 'auto',
                }}
              >
                {document.content.text}
              </Typography>
            </Box>
          )}
          {document.content && (
            <Typography fontSize={12} color="text.secondary">
              Это содержимое страницы на момент загрузки. Оно не меняет классификацию автоматически.
            </Typography>
          )}
        </>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <EditorOnly permissions="fakes.run">
        <Button
          variant="outlined"
          disabled={!data || busy || Boolean(pending)}
          onClick={() => void capture()}
        >
          {busy ? 'Запускаем…' : latest ? 'Обновить содержимое' : 'Получить содержимое'}
        </Button>
      </EditorOnly>
      {error && <Button onClick={() => setRevision((n) => n + 1)}>Перечитать состояние</Button>}
      {data && data.history.length > 1 && (
        <Box component="details">
          <Box component="summary" sx={{ cursor: 'pointer' }}>
            Последние загрузки ({data.history.length})
          </Box>
          {data.history.map((item) => (
            <Typography key={item.id} fontSize={12}>
              {fakeDate(item.createdAt)} · {labels[item.status]}
              {item.document?.error ? ' · ' + item.document.error.message : ''}
            </Typography>
          ))}
        </Box>
      )}
    </Stack>
  );
}
